import { DestroyRef, inject, Injectable } from '@angular/core';
import { map, Observable, skip, Subscription, take } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Layer from '@arcgis/core/layers/Layer';
import { MetricsService } from '@jeppesen-foreflight/jmkw-analytics';
import { PurlyService } from '@jeppesen-foreflight/jmkw-core';
import {
  BasicButtonComponent,
  ToolkitApiService
} from '@jeppesen-foreflight/jmkw-toolkit';
import {
  ButtonInstance,
  ButtonParams,
  JepVectorTileLayer,
  Plugin,
  PluginService,
  queueStyleLayerVisibility,
  ThemeColor
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { PluginIDs } from '../../../../plugin-ids.type';
import { getPurlyUrlForLayer } from '../../utilities/purly-url-mapping.util';
import {
  bindPurlyVectorLayer,
  PurlyLayerBinding
} from '../../utilities/purly-binding.util';

const id: PluginIDs = 'obstacles';

export const OBSTACLES_TOGGLE_BUTTON: ButtonParams = {
  id,
  iconName: 'icon-obstacle',
  buttonComponent: BasicButtonComponent,
  automationKey: 'obstacles-toggle-button',
  tooltipText: 'Obstacles',
  stateFn: (button, layer) => {
    button.active = layer.visible;
  },
  submit: (button: ButtonInstance) => {
    const layer = button.toolkitApiService.getLayerById<JepVectorTileLayer>(
      button.id
    );
    layer?.jepTools.toggleVisibility();
  }
};

@Injectable({
  providedIn: 'root'
})
export class ObstaclesPluginService implements PluginService {
  private readonly api = inject(ToolkitApiService);
  private readonly purlyService = inject(PurlyService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly metrics = inject(MetricsService);
  protected readonly logger = inject(NGXLogger);
  private readonly subscriptions = new Subscription();

  readonly id: PluginIDs = id;

  private _layerBinding: PurlyLayerBinding<JepVectorTileLayer> | undefined;

  private get obstacleLayer(): JepVectorTileLayer | undefined {
    return this._layerBinding?.layer;
  }

  private setupBasemapSubscription(): void {
    const layer = this.obstacleLayer;

    if (layer?.currentStyleInfo != undefined) {
      // Subscribe to theme changes to switch between Light/* and Dark/* style layers
      const sub = this.api
        .currentBasemap$()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((basemap: { theme: ThemeColor }) => {
          this.handleThemedRegex(basemap.theme);
        });

      this.subscriptions.add(sub);
    } else {
      // Layer style info not loaded yet, retry
      setTimeout(() => {
        this.setupBasemapSubscription();
      }, 100);
    }
  }

  create(): Observable<Plugin> {
    return this.purlyService.initializationResult$.pipe(
      take(1),
      map((result) => {
        // Prevent double initialization
        if (this._layerBinding) {
          this.logger.warn(
            'Obstacles plugin: Already initialized, returning existing binding'
          );
          return new Plugin({
            id,
            layer: this._layerBinding.layer as JepVectorTileLayer,
            button: OBSTACLES_TOGGLE_BUTTON
          });
        }

        // Setup binding utility for automatic URL refresh
        this._layerBinding = bindPurlyVectorLayer({
          purly$: this.purlyService.initializationResult$.pipe(skip(1)),
          toolkitApi: this.api,
          makeLayer: (url: string) =>
            new JepVectorTileLayer({
              id,
              zOrder: 1000,
              visible: true,
              label: 'Obstacles',
              url
            }),
          extractUrl: (result) => {
            const tileServiceUrls = this.purlyService.getTileServiceUrls();
            return getPurlyUrlForLayer('obstacles', tileServiceUrls);
          },
          pluginIds: { parent: 'aviation', child: id },
          logger: this.logger,
          initialResult: result
        });

        const layer = this._layerBinding.layer;

        if (!layer) {
          this.logger.warn(
            'Obstacles plugin: No layer created from binding utility'
          );
        }

        // Setup theme switching after layer is created
        this.setupBasemapSubscription();

        return new Plugin({
          id,
          layer: layer as JepVectorTileLayer,
          button: OBSTACLES_TOGGLE_BUTTON
        });
      })
    );
  }

  /**
   * Get current obstacles layer
   */
  getLayer(): JepVectorTileLayer | undefined {
    return this._layerBinding?.layer;
  }

  private createThemedRegex(theme: ThemeColor): RegExp {
    const themePrefix = theme === 'dark' ? 'Dark' : 'Light';
    return new RegExp(`^${themePrefix}/`);
  }
  private handleThemedRegex(theme: ThemeColor) {
    const layer = this.obstacleLayer;

    if (!layer) {
      return theme;
    }

    const currentThemeRegex = this.createThemedRegex(theme);
    const oppositeThemeRegex = this.createThemedRegex(
      theme === 'dark' ? 'light' : 'dark'
    );

    layer.currentStyleInfo?.style.layers.forEach((styleLayer: Layer) => {
      const matchesCurrent = currentThemeRegex.test(styleLayer.id);
      const matchesOpposite = oppositeThemeRegex.test(styleLayer.id);

      if (matchesCurrent) {
        queueStyleLayerVisibility(layer, styleLayer.id, 'visible');
      }
      if (matchesOpposite) {
        queueStyleLayerVisibility(layer, styleLayer.id, 'none');
      }
    });

    // No refresh() here. queueStyleLayerVisibility() applies the batched changes via a
    // single loadStyle(), which is what makes them take effect. refresh() would run
    // synchronously *before* that flush, forcing a full tile re-fetch against the old
    // style — pure waste, and the re-fetch is what made theme switching expensive with a
    // SceneView attached (JMKW-1859).

    return theme;
  }
}
